import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useView } from '../context/ViewContext';

const Dashboard = () => {
    const navigate = useNavigate();
    const { isOwnerView } = useView();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filter States
    const [filterDate, setFilterDate] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterTimeSlot, setFilterTimeSlot] = useState('');

    const fetchOrders = useCallback(async (signal) => {
        try {
            setLoading(true);
            const userStr = localStorage.getItem('user');
            if (!userStr) {
                navigate('/login');
                return;
            }
            const user = JSON.parse(userStr);

            let url = `https://rotica.in/get_orders.php?user_id=${user.id}&role=${isOwnerView ? 'owner' : user.role}`;

            // Append filters
            if (filterDate) url += `&date=${filterDate}`;
            if (filterStatus) url += `&status=${filterStatus}`;
            if (filterTimeSlot) url += `&time_slot=${encodeURIComponent(filterTimeSlot)}`;

            const response = await axios.get(url, { signal });

            if (response.data.success) {
                setOrders(response.data.orders);
                setError('');
            } else {
                setError(response.data.message || "Failed to fetch orders.");
            }
        } catch (err) {
            if (axios.isCancel(err)) return;
            console.error(err);
            setError("Network Error: Could not fetch orders.");
        } finally {
            if (!signal?.aborted) {
                setLoading(false);
            }
        }
    }, [navigate, filterDate, filterStatus, filterTimeSlot, isOwnerView]);

    useEffect(() => {
        const controller = new AbortController();
        fetchOrders(controller.signal);
        return () => controller.abort();
    }, [fetchOrders]);

    const handleLogout = () => {
        localStorage.removeItem('user');
        navigate('/login');
    };

    const clearFilters = () => {
        setFilterDate('');
        setFilterStatus('');
        setFilterTimeSlot('');
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return date.toLocaleDateString('en-IN', options);
    };

    const getDateLabel = (startDate, endDate) => {
        const today = new Date().toISOString().split('T')[0];
        if (startDate === today) {
            return <span className="date-badge today">Today</span>;
        } else if (startDate > today) {
            return <span className="date-badge upcoming">Upcoming</span>;
        }
        return <span className="date-badge active">Active</span>;
    };

    return (
        <div className="dashboard-container">
            <div className="main-content">
                <header className="dashboard-header">
                    <h1>Rotica Dashboard</h1>
                    <button className="logout-btn" onClick={handleLogout}>Logout</button>
                </header>

                <div className="stats-cards">
                    <div className="stat-card">
                        <h3>Total Orders</h3>
                        <p>{orders.length}</p>
                    </div>
                </div>

                <div className="orders-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h2>Orders Management</h2>
                    </div>

                    {/* Filters Bar */}
                    <div className="filters-bar">
                        <div className="filter-group">
                            <label>Date</label>
                            <input
                                type="date"
                                className="filter-input"
                                value={filterDate}
                                onChange={(e) => setFilterDate(e.target.value)}
                            />
                        </div>

                        <div className="filter-group">
                            <label>Status</label>
                            <select
                                className="filter-select"
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                            >
                                <option value="">All Statuses</option>
                                <option value="Pending">Pending</option>
                                <option value="Accepted">Accepted</option>
                                <option value="Assigned">Assigned</option>
                                <option value="Out for Delivery">Out for Delivery</option>
                                <option value="Delivered">Delivered</option>
                                <option value="Delayed">Delayed</option>
                                <option value="Cancelled">Cancelled</option>
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Time Slot</label>
                            <select
                                className="filter-select"
                                value={filterTimeSlot}
                                onChange={(e) => setFilterTimeSlot(e.target.value)}
                            >
                                <option value="">All Slots</option>
                                <option value="7-10 AM">7-10 AM</option>
                                <option value="12-3 PM">12-3 PM</option>
                                <option value="6-9 PM">6-9 PM</option>
                            </select>
                        </div>

                        <button className="clear-btn" onClick={clearFilters}>
                            Clear Filters
                        </button>
                    </div>

                    {loading && <p>Loading orders...</p>}
                    {error && <p className="error-text">{error}</p>}

                    {!loading && !error && (
                        <div className="table-wrapper">
                            <table className="orders-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Customer</th>
                                        {isOwnerView && <th>Assigned To</th>}
                                        <th>Items</th>
                                        <th>Subscription Period</th>
                                        <th>Delivery Info</th>
                                        <th>Status</th>
                                        <th>Remarks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.length > 0 ? (
                                        orders.map(order => (
                                            <tr key={order.id} onClick={() => navigate(`/order/${order.id}`)} style={{ cursor: 'pointer' }}>
                                                <td>#{order.id}</td>
                                                <td>
                                                    <div className="customer-info">
                                                        <span className="customer-name">{order.customer_name}</span>
                                                        <span className="customer-phone">{order.contact_no}</span>
                                                    </div>
                                                </td>
                                                {isOwnerView && (
                                                    <td>
                                                        <div className="entity-cell">
                                                            <span className="entity-name">{order.assigned_name || 'Unassigned'}</span>
                                                            <span className="entity-id" style={{ fontSize: '11px' }}>{order.assigned_type ? `ID: #${order.assigned_id}` : ''}</span>
                                                        </div>
                                                    </td>
                                                )}
                                                <td>
                                                    <span className="item-count-badge">
                                                        {order.items ? order.items.length : 0} Items
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="subscription-dates">
                                                        {getDateLabel(order.start_date, order.end_date)}
                                                        <div className="date-range">
                                                            <span className="date-label">Start:</span> {formatDate(order.start_date)}
                                                        </div>
                                                        <div className="date-range">
                                                            <span className="date-label">End:</span> {formatDate(order.end_date)}
                                                        </div>
                                                        {order.delay_days > 0 && (
                                                            <div className="date-range" style={{ color: '#d35400', fontSize: '11px', marginTop: '4px' }}>
                                                                Delayed: <strong>{order.delay_days} days</strong>
                                                                <br />
                                                                Resumes: {formatDate(order.resumption_date)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ fontSize: '13px' }}>
                                                        <div><strong>Time:</strong> {order.delivery_time}</div>
                                                        <div style={{ color: '#86868b', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{order.address_text}</div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`status-badge ${order.status?.toLowerCase().replace(/\s/g, '-')}`}>
                                                        {order.status || 'Pending'}
                                                    </span>
                                                </td>
                                                <td style={{ fontSize: '13px', color: '#86868b' }}>{order.remarks || '-'}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={isOwnerView ? "8" : "7"} style={{ textAlign: "center", padding: "40px" }}>
                                                No orders match your filters.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
